'use-client';

import { TNavigateToParams, useNavigation } from '@/lib/hooks/use-navigation';
import classNames from 'classnames';
import React, {
  useEffect,
  useRef,
  useState,
  cloneElement,
  ReactElement,
} from 'react';

/* ================================== TYPES ================================= */

export type TBottomNavBarProps = {
  items: {
    label: string;
    subLabel?: string;
    icon: ReactElement;
    navigateToParams?: TNavigateToParams;
    action: () => Promise<void> | void;
    showDividerAfter?: boolean;
  }[];
  iconSize?: 'sm' | 'md' | 'lg' | 'xl';
  theme?: 'dark' | 'light';
  blurBackground?: boolean;
  glowEffect?: boolean;
};

/* ========================================================================== */
/*                                  COMPONENT                                 */
/* ========================================================================== */
export function BottomNavBar({
  items,
  iconSize = 'md',
  theme = 'light',
  blurBackground = true,
  glowEffect = true,
}: TBottomNavBarProps) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const { navigateTo } = useNavigation();
  const [isOverflowing, setIsOverflowing] = useState(false);

  const navRef = useRef<HTMLDivElement | null>(null);

  const checkOverflow = () => {
    if (navRef.current) {
      const { clientWidth, scrollWidth } = navRef.current;
      setIsOverflowing(scrollWidth > clientWidth);
    }
  };

  useEffect(() => {
    checkOverflow();
    window.addEventListener('resize', checkOverflow);

    return () => window.removeEventListener('resize', checkOverflow);
  }, [items]);

  const handleItemClick = async (
    index: number,
    navigateToParams?: TBottomNavBarProps['items'][number]['navigateToParams'],
    action?: TBottomNavBarProps['items'][number]['action'],
  ) => {
    setSelectedIndex(index);

    if (navigateToParams) {
      navigateTo(navigateToParams);
    } else if (action) {
      await action();
    }
  };

  const navWrapperClasses = classNames(
    'w-full',
    'overflow-x-auto',
    'scrollbar-hide',
  );

  const itemsWrapperClasses = classNames(
    'flex',
    isOverflowing ? 'justify-start' : 'justify-center',
    'gap-4 px-4 py-4',
    blurBackground && 'backdrop-blur-md',
    theme === 'dark'
      ? 'bg-gray-900 text-white'
      : 'bg-infinityLight text-gray-900',
    'bg-opacity-60',
    'border-t-2',
    'border-infinityLight',
  );

  // Smooth horizontal scrolling
  const itemsWrapperStyles = {
    scrollSnapType: 'x mandatory',
    boxShadow: glowEffect ? 'rgba(255, 255, 255, 0.8) 0px 0px 30px' : 'none',
  };

  const buttonClasses = (index: number, showDividerAfter = false) =>
    classNames(
      'flex flex-col items-center',
      theme === 'dark' ? 'text-infinityLight' : 'text-infinity',
      'transition-colors duration-200 ease-in-out',
      'scroll-snap-align: start',
      showDividerAfter && 'border-r-2',
      showDividerAfter && theme === 'dark' && 'border-gray-400',
      showDividerAfter && theme === 'light' && 'border-gray-500',
      showDividerAfter && 'pr-4',
      showDividerAfter && 'mr-2',
    );

  const iconWrapperClasses = classNames(
    iconSize === 'sm' && 'h-6 w-6',
    iconSize === 'md' && 'h-10 w-10',
    iconSize === 'lg' && 'h-12 w-12',
    iconSize === 'xl' && 'h-14 w-14',
    'mb-1',
  );

  const iconClasses = classNames('h-full', 'w-full', 'text-4xl');

  const labelClasses = classNames('text-md', 'font-meyer');

  const subLabelClasses = classNames('text-xs', 'font-meyer');

  return (
    <div className={navWrapperClasses} style={{ overflow: 'visible' }}>
      <div
        ref={navRef}
        className={itemsWrapperClasses}
        style={itemsWrapperStyles}
      >
        {items.map((item, index) => (
          <React.Fragment key={index}>
            <button
              onClick={() =>
                void handleItemClick(index, item.navigateToParams, item.action)
              }
              className={buttonClasses(index, item.showDividerAfter)}
            >
              <div className={iconWrapperClasses}>
                {cloneElement(item.icon, { className: iconClasses })}
              </div>

              <span className={labelClasses}>{item.label}</span>

              {item.subLabel && (
                <span className={subLabelClasses}>{item.subLabel}</span>
              )}
            </button>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
